import { Link } from "react-router-dom";
import {
  describePhase3Progress,
  phase3OverallPercent,
  type Phase3Progress,
  type SynthesisLogStep,
} from "../lib/phase3Progress";
import type { Phase1JobPoll } from "../lib/phase1Job";
import { Phase1ProgressBar } from "./Phase1ProgressBar";
import { Icon } from "./Icon";

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="p-2 bg-surface-container rounded border border-outline-variant">
      <p className="text-[10px] uppercase text-on-surface-variant">{label}</p>
      <p className="text-lg font-semibold text-on-surface">{value}</p>
    </div>
  );
}

type Props = {
  runId: string;
  targetName?: string;
  contractorName?: string;
  jobStatus?: string;
  progress: Phase3Progress | null;
  log?: Phase1JobPoll["log"] | null;
  error?: string;
  className?: string;
  /** Compact layout for the bottom progress dock. */
  compact?: boolean;
};

export function Phase3SynthesisProgress({
  runId,
  targetName = "—",
  contractorName = "",
  jobStatus = "running",
  progress,
  log,
  error,
  className = "",
  compact = false,
}: Props) {
  const copy = describePhase3Progress(progress, jobStatus);
  const stepTotal = Number(progress?.queries_queued ?? 0);
  const stepDone = Number(progress?.queries_done ?? 0);
  const sourceCount = Number(progress?.source_count ?? 0);
  const overallPct = phase3OverallPercent(progress);
  const isDiscovering = progress?.phase === "discovering";
  const isQueued = jobStatus === "pending";

  const synthesisSteps = ((log as { steps?: SynthesisLogStep[] })?.steps ||
    []) as SynthesisLogStep[];

  if (error) {
    return (
      <section
        className={`flex flex-col p-6 panel-border rounded-lg bg-surface-container border-error/40 ${className}`}
      >
        <p className="font-semibold text-error">PDR synthesis failed</p>
        <p className="text-sm text-on-surface-variant mt-2 font-data-mono">{error}</p>
        <Link
          to={`/logs?run=${encodeURIComponent(runId)}`}
          className="text-primary text-xs hover:underline mt-4 inline-block"
        >
          View full log
        </Link>
      </section>
    );
  }

  return (
    <section
      className={`flex flex-col ${
        compact
          ? "p-0 bg-transparent"
          : "p-6 panel-border rounded-lg bg-surface-container border-tertiary/30"
      } ${className}`}
    >
      {!compact ? (
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {!isQueued && <span className="orion-spinner w-6 h-6 shrink-0" />}
            <div className="min-w-0">
              <p className="font-semibold text-on-surface">{copy.headline}</p>
              <p className="text-body-sm text-on-surface-variant truncate">
                {targetName !== "—" ? `${targetName}` : ""}
                {contractorName ? ` · ${contractorName}` : ""}
                {copy.detail ? ` · ${copy.detail}` : ""}
              </p>
            </div>
          </div>
          <Link
            to={`/logs?run=${encodeURIComponent(runId)}`}
            className="text-primary text-xs hover:underline shrink-0"
          >
            Full log
          </Link>
        </div>
      ) : null}

      {!isQueued && (
        <Phase1ProgressBar
          className={`shrink-0 ${compact ? "mb-2" : "mb-4"}`}
          percent={overallPct}
          label={compact ? "" : "Overall progress"}
          compact={compact}
          sublabel={
            stepTotal > 0
              ? `Step ${stepDone} of ${stepTotal}${compact ? "" : " (discovery + PDR JSON per project)"}`
              : undefined
          }
        />
      )}

      <div
        className={`grid text-center shrink-0 ${
          compact
            ? "grid-cols-4 gap-2 mb-2 text-[10px]"
            : "grid-cols-2 sm:grid-cols-4 gap-3 mb-4"
        }`}
      >
        <Stat label="Phase" value={copy.phaseLabel} />
        <Stat
          label="Steps"
          value={stepTotal > 0 ? `${stepDone} / ${stepTotal}` : "—"}
        />
        <Stat label="Sources (batch)" value={sourceCount || "—"} />
        <Stat
          label="Projects"
          value={`${progress?.projects_synthesized ?? 0} / ${progress?.projects_discovered ?? "—"}`}
        />
      </div>

      {!compact && isDiscovering && sourceCount > 0 ? (
        <div className="mb-4 p-3 rounded border border-tertiary/20 bg-tertiary/5 text-xs text-on-surface-variant shrink-0">
          <Icon name="batch_prediction" className="text-tertiary align-middle mr-1 text-base" />
          The <strong className="text-on-surface">{sourceCount}</strong> pending sources are
          read many at a time. The model first lists the projects they describe, matched to the
          known projects, then writes the PDR records, several projects per call.
        </div>
      ) : null}

      <div
        className={`flex-1 overflow-y-auto custom-scrollbar space-y-1 font-data-mono text-xs ${
          compact ? "min-h-0 max-h-24" : "min-h-[120px] max-h-[200px]"
        }`}
      >
        {synthesisSteps.length === 0 ? (
          <p className="text-on-surface-variant py-2">{copy.detail || "Starting…"}</p>
        ) : (
          synthesisSteps.slice(-14).map((s, i) => (
            <div
              key={`${s.kind}-${s.step}-${i}`}
              className="flex justify-between gap-2 text-on-surface-variant py-0.5"
            >
              <span className="truncate">
                {s.kind === "discovery"
                  ? "Discovery"
                  : s.project_name || "Project"}
                {s.source_count != null ? ` · ${s.source_count} src` : ""}
                {s.delta_count != null && s.kind === "project_synthesis"
                  ? ` · ${s.delta_count} field(s)`
                  : ""}
              </span>
              <span
                className={
                  s.status === "error" ? "text-error shrink-0" : "text-secondary shrink-0"
                }
              >
                {s.status || "ok"}
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
